import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';

const RabbitVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const [currentStage, setCurrentStage] = useState<'intro' | 'rabbit' | 'vortex' | 'choice'>('intro');
  const [introComplete, setIntroComplete] = useState(false);
  const [showRabbit, setShowRabbit] = useState(false);
  const [vortexComplete, setVortexComplete] = useState(false);
  const [currentText, setCurrentText] = useState<string>('');

  // Animation refs
  const textAnimationRef = useRef<number | null>(null);
  const typingPositionRef = useRef<number>(0);
  const targetTextRef = useRef<string>('');
  
  // Improved text typing effect with RAF
  const animateText = (text: string, delay: number = 40, callback?: () => void) => {
    // Cancel any ongoing animation
    if (textAnimationRef.current !== null) {
      cancelAnimationFrame(textAnimationRef.current);
      textAnimationRef.current = null;
    }
    
    // Reset state
    setCurrentText('');
    typingPositionRef.current = 0;
    targetTextRef.current = text;
    
    // Typing animation frame
    const typeNextChar = () => {
      if (typingPositionRef.current < targetTextRef.current.length) {
        const nextPosition = Math.min(typingPositionRef.current + 1, targetTextRef.current.length);
        const currentText = targetTextRef.current.substring(0, nextPosition);
        setCurrentText(currentText);
        
        // Play sound on certain frames
        if (typingPositionRef.current % 5 === 0) {
          SoundManager.playSound('keypress');
        }
        
        typingPositionRef.current = nextPosition;
        textAnimationRef.current = requestAnimationFrame(() => {
          // Add a deliberate delay between frames
          setTimeout(typeNextChar, delay);
        });
      } else {
        // Animation complete
        textAnimationRef.current = null;
        if (callback) callback();
      }
    };
    
    // Start animation
    textAnimationRef.current = requestAnimationFrame(typeNextChar);
    return () => {
      if (textAnimationRef.current !== null) {
        cancelAnimationFrame(textAnimationRef.current);
      }
    };
  };

  // Initialize scene with terminal and intro
  useEffect(() => {
    // Start the intro sequence with text typing animation
    const sequence = async () => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // First message
      await new Promise(resolve => {
        animateText("wake up, user.", 50, () => resolve(undefined));
      });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Second message
      await new Promise(resolve => {
        animateText("you've been searching in the wrong places.", 50, () => resolve(undefined));
      });
      
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Third message
      await new Promise(resolve => {
        animateText("the signal is weak, but we see you.", 50, () => resolve(undefined));
      });
      
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Fourth message
      await new Promise(resolve => {
        animateText("we've left a trace.\nyou must follow it.", 50, () => resolve(undefined));
      });
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Final message
      await new Promise(resolve => {
        animateText("look.", 80, () => resolve(undefined));
      });
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Show the rabbit
      setIntroComplete(true);
      setShowRabbit(true);
    };
    
    sequence();
    
    return () => {
      if (textAnimationRef.current !== null) {
        cancelAnimationFrame(textAnimationRef.current);
      }
    };
  }, []);
  
  // Create Three.js scene for vortex
  useEffect(() => {
    if (currentStage !== 'vortex' || !containerRef.current) return;
    
    // Setup Three.js
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 5;
    
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Create vortex tunnel with TubeGeometry
    const createVortexTunnel = () => {
      // Curve defining the tunnel path
      class CustomSinCurve extends THREE.Curve<THREE.Vector3> {
        scale: number;
        
        constructor(scale = 1) {
          super();
          this.scale = scale;
        }
        
        getPoint(t: number, optionalTarget = new THREE.Vector3()) {
          const tx = t * 3 - 1.5;
          const ty = Math.sin(t * Math.PI * 2) * 0.5;
          const tz = Math.cos(t * Math.PI * 2) * 0.5;
          
          return optionalTarget.set(tx, ty, tz).multiplyScalar(this.scale);
        }
      }
      
      const path = new CustomSinCurve(10);
      const geometry = new THREE.TubeGeometry(path, 100, 2, 16, false);
      
      // Create gradient for the tunnel
      const colors = [];
      const color1 = new THREE.Color(0x00ff00);
      const color2 = new THREE.Color(0x8a2be2);
      
      for (let i = 0; i < geometry.attributes.position.count; i++) {
        const t = i / geometry.attributes.position.count;
        const color = new THREE.Color().lerpColors(color1, color2, t);
        colors.push(color.r, color.g, color.b);
      }
      
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      
      const material = new THREE.MeshBasicMaterial({
        wireframe: true,
        vertexColors: true,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide
      });
      
      const tunnel = new THREE.Mesh(geometry, material);
      return tunnel;
    };
    
    const vortexTunnel = createVortexTunnel();
    scene.add(vortexTunnel);
    
    // Add particles to the vortex
    const particlesCount = 2000;
    const particlesGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particlesCount * 3);
    const colors = new Float32Array(particlesCount * 3);
    
    for (let i = 0; i < particlesCount; i++) {
      // Position
      const theta = Math.random() * Math.PI * 2;
      const radius = Math.random() * 2;
      const zPos = (Math.random() * 20) - 10;
      
      positions[i * 3] = Math.cos(theta) * radius;
      positions[i * 3 + 1] = Math.sin(theta) * radius;
      positions[i * 3 + 2] = zPos;
      
      // Color
      const color = new THREE.Color();
      color.setHSL(0.1 + 0.5 * Math.random(), 1.0, 0.5);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });
    
    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particles);
    
    // Add floating message during vortex
    let messageOpacity = 0;
    const message = "This is not a game. But you already knew that.";
    
    const showFloatingMessage = () => {
      if (textContainerRef.current) {
        textContainerRef.current.style.opacity = messageOpacity.toString();
        setCurrentText(message);
      }
    };
    
    // Animation variables
    let cameraProgress = 0;
    let vortexDuration = 10; // seconds
    let startTime = Date.now();
    
    // Animation loop
    const animate = () => {
      const animationId = requestAnimationFrame(animate);
      
      // Calculate progress through the vortex
      const elapsedTime = (Date.now() - startTime) / 1000;
      const progress = Math.min(elapsedTime / vortexDuration, 1);
      
      // Update camera position
      cameraProgress = progress * 15 - 5; // -5 to 10
      camera.position.z = 5 - cameraProgress;
      
      // Rotate vortex
      vortexTunnel.rotation.z += 0.01;
      
      // Make particles swirl
      const positions = particles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particlesCount; i++) {
        const idx = i * 3;
        const x = positions[idx];
        const y = positions[idx + 1];
        const z = positions[idx + 2];
        
        // Rotate particles around the tunnel
        const angle = 0.01;
        positions[idx] = x * Math.cos(angle) - y * Math.sin(angle);
        positions[idx + 1] = x * Math.sin(angle) + y * Math.cos(angle);
        
        // Move particles along the tunnel
        positions[idx + 2] = z - 0.05;
        
        // Reset particles that go too far
        if (positions[idx + 2] < -10) {
          positions[idx + 2] = 10;
        }
      }
      particles.geometry.attributes.position.needsUpdate = true;
      
      // Show message halfway through
      if (progress > 0.3 && progress < 0.8) {
        messageOpacity = Math.min(1, messageOpacity + 0.01);
      } else {
        messageOpacity = Math.max(0, messageOpacity - 0.01);
      }
      showFloatingMessage();
      
      // End of vortex sequence
      if (progress >= 1) {
        cancelAnimationFrame(animationId);
        
        // Clean up
        if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
          containerRef.current.removeChild(renderer.domElement);
        }
        
        // Move to next stage
        setTimeout(() => {
          setVortexComplete(true);
          setCurrentStage('choice');
          
          // Show the final message after a slight delay
          setTimeout(() => {
            animateText(
              "You crossed the line.\n\nThere is no going back.\n\nEverything you believe is a program.\nEverything you fear is a firewall.\n\nNow choose:",
              40
            );
          }, 500);
        }, 500);
      }
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Create a low frequency ambient sound
    if (currentStage === 'vortex') {
      SoundManager.playSound('command');
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [currentStage]);
  
  // Handler for rabbit click
  const handleRabbitClick = () => {
    SoundManager.playSound('command');
    setCurrentStage('vortex');
  };
  
  // Handler for choice buttons
  const handleChoice = (choice: 'remember' | 'erase' | 'exit') => {
    SoundManager.playSound('command');
    
    if (choice === 'remember') {
      // Link to hidden page
      window.location.href = '/hidden';
    } else if (choice === 'erase') {
      // Simulate reboot glitch
      setCurrentText('');
      setTimeout(() => {
        animateText("SYSTEM REBOOT INITIATED...\n\nERASING MEMORY BUFFER...\n\nRESETTING PARAMETERS...", 20, () => {
          setTimeout(() => {
            // Reset to beginning
            setCurrentStage('intro');
            setIntroComplete(false);
            setShowRabbit(false);
            setVortexComplete(false);
            setCurrentText('');
            
            // Restart intro sequence
            setTimeout(() => {
              animateText("wake up, user.", 50, () => {
                setTimeout(() => {
                  setShowRabbit(true);
                }, 1000);
              });
            }, 1000);
          }, 1000);
        });
      }, 500);
    } else if (choice === 'exit') {
      // Return to main site
      window.history.back();
    }
  };
  
  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative bg-black text-green-500 font-mono">
      {/* Main container for 3D and content */}
      <div 
        ref={containerRef} 
        className="absolute inset-0 flex items-center justify-center"
      >
        {/* Text container */}
        <div 
          ref={textContainerRef}
          className="z-10 relative max-w-2xl text-center p-8 transition-opacity"
        >
          <pre className="whitespace-pre-line text-xl">
            {currentText}
            {currentStage !== 'vortex' && <span className="animate-pulse ml-1">█</span>}
          </pre>
        </div>
        
        {/* White rabbit */}
        {showRabbit && currentStage === 'rabbit' && (
          <div 
            className="absolute bottom-8 right-8 cursor-pointer animate-pulse"
            onClick={handleRabbitClick}
          >
            <div className="w-12 h-12 bg-white rounded-full opacity-80 shadow-lg shadow-white flex items-center justify-center text-3xl">
              🐇
            </div>
          </div>
        )}
        
        {/* Choice buttons */}
        {currentStage === 'choice' && vortexComplete && (
          <div className="absolute bottom-32 left-0 right-0 flex flex-col items-center gap-4 mt-8">
            <button
              onClick={() => handleChoice('remember')}
              className="px-8 py-3 bg-transparent border-2 border-green-500 text-green-500 hover:bg-green-900 hover:text-white transition-colors rounded-sm w-64 shadow-lg shadow-green-500/30"
            >
              REMEMBER
            </button>
            <button
              onClick={() => handleChoice('erase')}
              className="px-8 py-3 bg-transparent border-2 border-red-500 text-red-500 hover:bg-red-900 hover:text-white transition-colors rounded-sm w-64 shadow-lg shadow-red-500/30"
            >
              ERASE
            </button>
            <button
              onClick={() => handleChoice('exit')}
              className="px-8 py-3 bg-transparent border-2 border-blue-500 text-blue-500 hover:bg-blue-900 hover:text-white transition-colors rounded-sm w-64 shadow-lg shadow-blue-500/30"
            >
              EXIT
            </button>
          </div>
        )}
      </div>
      
      {/* Black overlay for intro */}
      {currentStage === 'intro' && (
        <div className="absolute inset-0 bg-black z-20 flex items-center justify-center">
          <div className="max-w-2xl text-xl text-green-500 p-8">
            <pre className="whitespace-pre-line">
              {currentText}
              <span className="animate-pulse ml-1">█</span>
            </pre>
          </div>
        </div>
      )}
      
      {/* Update stage when intro completes */}
      {introComplete && currentStage === 'intro' && (
        <div className="hidden">
          {(() => {
            setCurrentStage('rabbit');
            return null;
          })()}
        </div>
      )}
    </div>
  );
};

export default RabbitVisualization;